import { scrollToId } from "@/App";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import BrandMark from "./BrandMark";
import { openCookiePreferences } from "./CookieConsent";

const XLogo = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
  </svg>
);

const TikTokLogo = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const YouTubeLogo = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const LinktreeLogo = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M7.953 2.512v4.06H3.332v3.069h4.621v9.845h3.836V9.641h4.621V6.572h-4.621V2.512H7.953Zm8.836 12.889a2.581 2.581 0 1 0 .002 5.162 2.581 2.581 0 0 0-.002-5.162Z" />
  </svg>
);

const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/airobotcamstudio", Icon: Linkedin },
  { label: "Instagram", href: "https://www.instagram.com/airobotcamstudiox", Icon: Instagram },
  { label: "TikTok", href: "https://www.tiktok.com/@airobotcamstudio", Icon: TikTokLogo },
  { label: "YouTube", href: "https://youtube.com/@airobotcamstudiox?si=6-uBJTQOW9vy2_uf", Icon: YouTubeLogo },
  { label: "X", href: "https://x.com/ArthurP750745", Icon: XLogo },
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61581167319803", Icon: Facebook },
  { label: "Linktree", href: "https://linktr.ee/airobotcamstudio", Icon: LinktreeLogo },
];

const NAV = [
  { title: "Explore", links: [["Home", "home"], ["Technology", "technology"], ["Demo", "demo"], ["Aria Live", "aria-live"], ["MVP", "mvp"], ["Roadmap", "roadmap"], ["Updates", "updates"]] },
  { title: "Company", links: [["About", "about"], ["Investment", "investment"], ["Contact", "contact"]] },
];

const LEGAL = [
  ["Terms of Service", "/terms-of-service", "terms"],
  ["Privacy Policy", "/privacy-policy", "privacy"],
  ["Fair Use Policy", "/fair-use-policy", "fair-use"],
  ["Cookie Policy", "/cookie-policy", "cookie"],
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-black">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          <div>
            <BrandMark className="h-12 w-12 rounded-lg" testid="footer-logo" />
            <p className="mt-4 font-display text-sm font-semibold tracking-[0.25em] text-white">
              AIROBOTCAM<span className="text-cyan-300">STUDIO</span>
            </p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/70">
              A UK technology startup developing a new generation of interactive AI entertainment —
              real-time videochat with photorealistic AI virtual companions.
            </p>
            <div data-testid="footer-company-number" className="mt-4 space-y-1.5 text-[11px] uppercase tracking-[0.15em] text-white/55">
              <p>UK: AIROBOTCAMSTUDIO LTD · Company No. 17410621 · England and Wales</p>
              <p>US: AI ROBOT CAM STUDIO LLC · Delaware File No. 10553824 · Non-trading</p>
            </div>
          </div>
          {NAV.map(({ title, links }) => (
            <div key={title}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">{title}</p>
              <div className="mt-5 flex flex-col gap-3">
                {links.map(([label, id]) => (
                  <button
                    key={id}
                    data-testid={`footer-link-${id}`}
                    onClick={() => scrollToId(id)}
                    className="w-fit text-sm text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">Legal</p>
            <div className="mt-5 flex flex-col gap-3">
              {LEGAL.map(([label, href, id]) => (
                <a
                  key={id}
                  data-testid={`footer-link-legal-${id}`}
                  href={href}
                  className="w-fit text-sm text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100"
                >
                  {label}
                </a>
              ))}
              <button
                data-testid="footer-cookie-preferences"
                onClick={openCookiePreferences}
                className="w-fit text-left text-sm text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100"
              >
                Cookie Preferences
              </button>
            </div>
          </div>
        </div>

        <div
          data-testid="footer-socials"
          className="mt-12 flex flex-wrap items-center justify-center gap-3 sm:justify-start"
        >
          {SOCIALS.map(({ label, href, Icon }) => (
            <a
              key={label}
              data-testid={`footer-social-${label.toLowerCase()}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`AIROBOTCAMSTUDIO on ${label}`}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-cyan-300/20 text-cyan-200/70 transition-colors duration-300 hover:border-cyan-300/50 hover:text-cyan-100"
            >
              <Icon size={16} />
            </a>
          ))}
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <p className="max-w-4xl text-[11px] leading-relaxed text-white/50">
            All characters shown on this website are AI-generated and entirely fictional. Product
            visuals are conceptual and represent an experience currently in development. This
            website is provided for information purposes only and does not constitute an offer of
            securities or investment advice. The fundraising round is in preparation and no
            investment has been secured. SEIS Advance Assurance is subject to HMRC approval.
          </p>
          <p className="mt-6 text-xs text-white/55">© 2026 AIROBOTCAMSTUDIO LTD. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
